import React from 'react';

import { Messages } from '~src/codegen/Localize';
import { imgPath } from '~src/commonInfra/utils/ImgUrl';
import { DraftTags } from '~src/drafts/components/DraftTags';
import { Draft, DraftTagsParentType } from '~src/drafts/types/DraftsTypes';
import { isUnpublishedDraft, isValidDraftStatus } from '~src/drafts/utils/DraftUtils';
import { FavoriteToggle } from '~src/favorites/components/favoriteToggle/FavoriteToggle';
import { CardViewStyles } from '~src/spacesPlatform/components/card/CardViewStyles';
import { ContentCard, getContentUrlForItem } from '~src/spacesPlatform/components/card/ContentCard';
import { DetailsTooltipStyles } from '~src/spacesPlatform/components/card/DetailsTooltipStyles';
import { Thumbnail } from '~src/spacesPlatform/components/card/Thumbnail';
import { doesTooltipNotHaveOwnerToDisplay } from '~src/spacesPlatform/components/tooltip/Tooltip';
import { TooltipAnchor } from '~src/spacesPlatform/components/tooltip/TooltipAnchor';
import { ExplorableItemType } from '~src/spacesPlatform/models/ContentType';

import { WorkbookTooltip } from '../WorkbookTooltip';

export const WorkbookDefaultImageUrl = imgPath('spaces_card_workbook.svg');

export class WorkbookCard extends ContentCard {

  public render() {
    const disableAutoFocus = doesTooltipNotHaveOwnerToDisplay(this.props.content);
    const isValidDraft = isValidDraftStatus(this.props.content.draftStatus);
    const isUnpublished = isUnpublishedDraft(this.props.content.draftStatus);

    return (
      <div className={CardViewStyles.Card}>
        <TooltipAnchor
          tooltipContent={this.renderTooltipContent}
          width={DetailsTooltipStyles.Width}
          onShowTooltip={this.handleShowTooltip}
          title={Messages.workbookDetails()}
          disableAutoFocus={disableAutoFocus}
          disableTooltip={this.props.disableTooltip}
        >
          <Thumbnail navigateUrl={this.getNavigationUrl()}
            defaultImageUrl={WorkbookDefaultImageUrl}
            thumbnailImageUrl={this.props.content.thumbnailUrl}
            decorate={true}
            allowDragAndDrop={this.props.allowDragAndDrop}
            hoveredOrSelected={this.props.hoveredOrSelected}
            draftStatus={this.props.content.draftStatus}
            aria-label={this.props.content.name}
          />
        </TooltipAnchor>
        <div className={CardViewStyles.ActionRow}>
          <span data-tb-test-id="workbook-name" className={CardViewStyles.ActionRowName} title={this.props.content.name}>
            { this.props.content.name }
          </span>
          { !isUnpublished && this.renderFavoriteToggle() }
          { this.renderActionsMenu() }
        </div>
        <div className={CardViewStyles.CardDescription}>
          {isValidDraft ? this.getDescriptionWithDraft() : this.getDescription()}
        </div>
        {isValidDraft && this.renderDraftStatus(this.props.content.draftStatus, this.props.content.contentType)}
      </div>
    );
  }

  private renderTooltipContent = () => {
    return <WorkbookTooltip content={this.props.content}
                            urls={this.props.urls}
                            inProjectSpace={this.props.inProjectSpace}
                            inPersonalSpace={this.props.inPersonalSpace}
                            draftLastUpdatedTimeStamp={this.getDraft()?.updatedAt}/>;
  };

  private renderDraftStatus(draftStatus: string, contentType: ExplorableItemType) {
    return (
      <DraftTags
        explorableItemType={contentType}
        draftStatus={draftStatus}
        parentType={DraftTagsParentType.card}>
      </DraftTags>
    );
  }

  private renderFavoriteToggle(): JSX.Element {
    return (
      <span>
        <FavoriteToggle
          isTabbable={true}
          isFavorite={this.props.content.favorite}
          onAddFavorite={() => this.props.addContentFavorite({ ...this.props.content, contentType: 'workbook' })}
          onRemoveFavorite={() => this.props.removeContentFavorite({ ...this.props.content, contentType: 'workbook' })}
        />
      </span>
    );
  }

  private getNavigationUrl = (): string | undefined => {
    const { content, spaceInfo } = this.props;

    if (isUnpublishedDraft(content.draftStatus)) {
      const parentId = this.getDraft()?.parentId;
      const projectId = spaceInfo && spaceInfo.spaceType === 'project' ? spaceInfo.item.id : undefined;
      return this.props.urls.unpublishedWorkbookUrl(parentId, projectId);
    }

    return getContentUrlForItem(this.props.content, this.props.urls);
  };

  private getDraft = (): Draft | undefined => {
    const { draftLuid } = this.props.content;
    return (draftLuid && this.props.drafts && draftLuid in this.props.drafts) ? this.props.drafts[draftLuid] : undefined;
  };
}
